import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageHeader from "@/components/marketing/PageHeader";
import { IMG, PROJECTS, PROJECT_CATEGORIES } from "@/data/content";
import { useSiteContent } from "@/lib/siteContent";

const categories = ["All", "Highway & FDOT", "Commercial & Site Work", "Airports & Municipal", "Residential & HOA"];
const marketPages = [
  { slug: "highway-fdot", title: "Heavy Highway & FDOT Corridors", text: "Interstate, turnpike, corridor, and county roadway work delivered to FDOT standards." },
  { slug: "commercial-industrial", title: "Commercial & Industrial Site Work", text: "Complete civil site development, utilities, grading, and paving for complex private-sector projects." },
  { slug: "airports-municipal", title: "Aviation & Municipal Infrastructure", text: "FAA-focused airfield paving alongside municipal streets, port, and public-infrastructure projects." },
  { slug: "residential-hoa", title: "Residential & HOA Communities", text: "Community infrastructure, access roads, and resurfacing programs built for long-term performance." },
];

export default function ProjectsPage() {
  const { category } = useParams();
  const site = useSiteContent();
  const landing = PROJECT_CATEGORIES[category];
  const routeSelection = landing?.label || "All";
  const [selected, setSelected] = useState(routeSelection);
  const projects = Array.isArray(site.projects) && site.projects.length ? site.projects : PROJECTS;

  useEffect(() => setSelected(routeSelection), [routeSelection]);

  const items = useMemo(
    () => projects.filter((project) => selected === "All" || project.category === selected),
    [projects, selected],
  );

  if (category === "markets") {
    return <div data-testid="project-market-hub">
      <PageHeader overline="Projects / Portfolio" crumb="Filter by Market" title="Filter Projects by Market" subtitle="Explore Ranger experience by the type of infrastructure and community we serve." image={IMG.ellison} />
      <section className="bg-rc-bg py-16 lg:py-24"><div className="max-w-6xl mx-auto px-5 lg:px-8 grid md:grid-cols-2 gap-5">
        {marketPages.map((market) => <Link key={market.slug} to={`/projects/${market.slug}`} className="bg-white border border-black/10 rounded-2xl p-8 hover:border-rc-red transition-colors">
          <h2 className="font-heading font-black text-2xl text-rc-blue">{market.title}</h2><p className="mt-3 text-rc-brown leading-relaxed">{market.text}</p><span className="mt-5 inline-block text-rc-red font-bold">View projects →</span>
        </Link>)}
      </div></section>
    </div>;
  }

  return (
    <div data-testid="projects-page">
      <PageHeader
        overline="Projects / Portfolio"
        crumb="Projects"
        title="Proven Civil Infrastructure Across Florida"
        subtitle={landing?.description || "Explore our completed and active heavy highway, site development, aviation, and commercial paving projects."}
        image={IMG.ellison}
      />
      <section className="bg-rc-bg py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-wrap gap-2 mb-10" aria-label="Filter projects by market">
            {categories.map((item) => (
              <button
                type="button"
                key={item}
                onClick={() => setSelected(item)}
                aria-pressed={selected === item}
                className={`rounded-full px-4 py-2 text-sm font-semibold ${selected === item ? "bg-rc-red text-white" : "bg-white border border-black/10 text-rc-charcoal"}`}
              >
                {item}
              </button>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {items.map((project) => (
              <article key={project.title} className="relative rounded-2xl overflow-hidden bg-rc-charcoal aspect-[16/9]">
                {project.img && <img src={project.img} alt="" className="absolute inset-0 w-full h-full object-cover" />}
                <div className="absolute inset-0 bg-gradient-to-t from-rc-charcoal/90 to-transparent" />
                <div className="absolute bottom-0 p-7 text-white">
                  <p className="text-rc-sand text-xs uppercase tracking-widest">{project.category} · {project.location}</p>
                  <h2 className="font-heading text-2xl font-black mt-2">{project.title}</h2>
                  <p className="text-white/70">{project.type}</p>
                </div>
              </article>
            ))}
          </div>
          {items.length === 0 && (
            <p className="bg-white border border-black/10 rounded-2xl p-8 text-rc-brown">
              Contact our estimating team to discuss experience relevant to your project.
            </p>
          )}
          <div className="bg-rc-charcoal rounded-2xl mt-12 p-8 text-white flex flex-wrap items-center justify-between gap-5">
            <h2 className="font-heading text-2xl font-black">Have an upcoming civil project?</h2>
            <Link to="/contact/request-a-bid" className="bg-rc-red px-6 py-3 rounded-full font-bold">Send RFP to Estimating</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
